import { OrderAPI } from "@/apis/tms/order";
import Indicator from "@/components/shared/Indicator";
import PdfViewerModal from "@/components/shared/PdfViewer";
import {
  DialogRef,
  TMSOrderStatus,
  TMSScanOrderConfig,
  TMSShippingLabelMode,
} from "@/types";
import { EyeOutlined } from "@ant-design/icons";
import { Button, Descriptions } from "antd";
import { DescriptionsItemProps } from "antd/es/descriptions/Item";
import { useMemo, useRef } from "react";
import CreateShippingLabelsDialog from "./CreateLabels";
import DeleteShippingLabelsDialog from "./DeleteLabels";

interface ScanInfoProps {
  className?: string;
  orderId: string;
  orderStatus: TMSOrderStatus;
  scanConfig: TMSScanOrderConfig;
  onUploadDone: () => void;
}

export function ScanInfo(props: ScanInfoProps) {
  const { orderId, orderStatus, scanConfig, onUploadDone } = props;
  const pdfViewerModalRef = useRef<DialogRef>(null);
  const createLabelsDialogRef = useRef<DialogRef>(null);
  const deleteLabelsDialogRef = useRef<DialogRef>(null);
  const infoItems = useMemo(() => {
    const items: DescriptionsItemProps[] = [];
    items.push(
      ...[
        {
          label: "Has Carton Label",
          children: scanConfig.hasCartonLabel ? "Yes" : "No",
        },
        {
          label: "Shipping Label",
          children: scanConfig.shippingLabelMode,
        },
        {
          label: "SSCC Order",
          children: scanConfig.isSSCCOrder ? "Yes" : "No",
        },
      ],
    );
    return items;
  }, [scanConfig]);

  return (
    <div>
      <div className="flex flex-row items-center gap-2">
        <Indicator label="Scan Info" />
        {scanConfig.shippingLabelMode === TMSShippingLabelMode.Pallet &&
          (scanConfig.uploadedPalletShippingLabels ? (
            <div className="flex flex-row gap-2 items-center">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() =>
                  pdfViewerModalRef.current?.showModal({
                    orderId: orderId,
                    scanConfig: scanConfig,
                  })
                }
              >
                Preview Shipping Labels
              </Button>
              <div>
                <Button
                  size="small"
                  type="primary"
                  danger
                  onClick={() =>
                    deleteLabelsDialogRef.current?.showModal({
                      orderId: orderId,
                    })
                  }
                >
                  Delete Shipping Labels
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="small"
              type="primary"
              onClick={() =>
                createLabelsDialogRef.current?.showModal({
                  orderId: orderId,
                })
              }
            >
              Generate Shipping Labels
            </Button>
          ))}

        {scanConfig.shippingLabelMode === TMSShippingLabelMode.Carton && (
          <div className="flex flex-row gap-2">
            {orderStatus === TMSOrderStatus.Created && (
              <Button
                size="small"
                type="primary"
                onClick={() =>
                  createLabelsDialogRef.current?.showModal({
                    orderId: orderId,
                  })
                }
              >
                Create Shipping Labels
              </Button>
            )}
            {orderStatus !== TMSOrderStatus.Deleted &&
              orderStatus !== TMSOrderStatus.Created && (
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() =>
                    pdfViewerModalRef.current?.showModal({
                      orderId: orderId,
                      scanConfig: scanConfig,
                    })
                  }
                >
                  Preview Shipping Labels
                </Button>
              )}
            {orderStatus === TMSOrderStatus.Processing && (
              <div>
                <Button
                  size="small"
                  type="primary"
                  danger
                  onClick={() =>
                    deleteLabelsDialogRef.current?.showModal({
                      orderId: orderId,
                    })
                  }
                >
                  Delete Shipping Labels
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <Descriptions
        column={2}
        size="small"
        bordered
        items={infoItems}
        styles={{
          label: { width: "16%" },
          content: { width: "36%" },
        }}
      />
      <PdfViewerModal
        ref={pdfViewerModalRef}
        url={`${OrderAPI.config.baseURL}/scan/${orderId}/shipping/labels.pdf`}
      />
      <CreateShippingLabelsDialog
        ref={createLabelsDialogRef}
        onCreated={onUploadDone}
      />
      <DeleteShippingLabelsDialog
        ref={deleteLabelsDialogRef}
        onDeleted={onUploadDone}
      />
    </div>
  );
}
